import { Injectable, computed, inject } from '@angular/core'
import { BehaviorSubject, firstValueFrom } from 'rxjs'
import { StorageService } from '@everyday-speech/eds-design-system-lib'
import { StorageKeys } from '@app/common/types/storage-keys'
import { UserService } from '@app/user-management/services/user.service'
import { User } from '@app/user-management/types/user'
import { AuthenticationService } from '@app/auth-management/authentication.service'
import { toSignal } from '@angular/core/rxjs-interop'
import { AccessRole } from '@app/user-management/types/access-role'

@Injectable({
  providedIn: 'root',
})
export class LocalUserService {
  private readonly storageService = inject(StorageService)
  private readonly userService = inject(UserService)
  private readonly authenticationService = inject(AuthenticationService)

  private readonly _activeUser$ = new BehaviorSubject<User | undefined>(undefined)
  public readonly activeUser$ = this._activeUser$.asObservable()
  public readonly activeUserSignal = toSignal(this.activeUser$, { requireSync: true })

  public readonly isSpeechyAdmin = computed(() => this.activeUserSignal()?.accessRole === AccessRole.SPEECHY_ADMIN)

  constructor() {
    this.setupUser()
  }

  setupUser() {
    // if local user is already set we user data from local storage
    const localUserData = this.storageService.read<string>(StorageKeys.ACTIVE_USER_STORAGE_KEY)
    if (localUserData) {
      const userFromStorage: User = Object.assign(new User(), JSON.parse(atob(localUserData)))
      this._activeUser$.next(userFromStorage)
      return
    }

    // if local user is not set we fetch data from server using userId value from token
    this.authenticationService.identityToken$.subscribe(token => {
      if (token) {
        this.fetchAndSetActiveUser(token)
      } else {
        this._activeUser$.next(undefined)
        this.storageService.remove(StorageKeys.ACTIVE_USER_STORAGE_KEY)
      }
    })
  }

  private async fetchAndSetActiveUser(token: string) {
    const payload = this.decodeToken(this.getIdentityTokenPayload(token))
    if (payload?.userId) {
      const userFromServer = await firstValueFrom(this.userService.getUserById(payload.userId))
      this.setActiveUser(userFromServer)
    }
  }

  private setActiveUser(user: User | undefined) {
    this._activeUser$.next(user)
    this.storageService.put(StorageKeys.ACTIVE_USER_STORAGE_KEY, btoa(JSON.stringify(user)))
  }

  private getIdentityTokenPayload(token: string): string | undefined {
    return token ? token.split('.')[1] : undefined
  }

  /** JWT segments are base64url (`-`/`_`, padding often stripped); atob expects standard base64. */
  private base64UrlDecode(segment: string): string {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    return atob(padded)
  }

  private decodeToken(token: string | undefined): { userId?: string } | undefined {
    if (!token) {
      return undefined
    }
    // Fail closed on a malformed token: no userId means the active-user fetch is
    // skipped rather than the caller throwing.
    try {
      const decoded = JSON.parse(this.base64UrlDecode(token))
      // v3 Cognito session tokens (SSO + password login) nest the real payload as a
      // JSON string under `data` — mirror slp-user-api's encodeSessionCognito /
      // decodeUserCognito. The legacy `?token=` handoff carries a flat payload, so
      // only unwrap when `data` is a valid JSON string, else fall back to the outer object.
      if (typeof decoded?.data === 'string') {
        try {
          return JSON.parse(decoded.data)
        } catch {
          return decoded
        }
      }
      return decoded
    } catch {
      return undefined
    }
  }
}
